/* eslint-disable @typescript-eslint/no-explicit-any */

import { camelToSnake, snakeToCamel } from '@ben/orion/utils/converter';
import * as Sentry from '@sentry/react';
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import baseAxios from 'axios';
import { captureError } from './capture-error';

const getPayloadSizeBytes = (data: unknown): number => {
  if (!data) return 0;
  if (typeof data === 'string') return new Blob([data]).size;
  if (data instanceof FormData) {
    let size = 0;
    for (const value of data.values()) {
      size +=
        value instanceof Blob ? value.size : new Blob([String(value)]).size;
    }
    return size;
  }
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
};

const BYTES_PER_MB = 1024 * 1024;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < BYTES_PER_MB) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(2)}MB`;
};

const networkErrorInterceptor = (error: AxiosError): Promise<never> => {
  const isNetworkError = !error.response && error.message === 'Network Error';
  const isTimeoutError = error.code === 'ECONNABORTED';

  if (isNetworkError || isTimeoutError) {
    const payloadSizeBytes = getPayloadSizeBytes(error.config?.data);

    Sentry.withScope((scope) => {
      scope.setTag('error_type', isTimeoutError ? 'timeout' : 'network_error');
      scope.setTag('request_url', error.config?.url ?? 'unknown');
      scope.setTag(
        'request_method',
        error.config?.method?.toUpperCase() ?? 'unknown'
      );
      scope.setLevel('error');

      scope.setContext('request_details', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`,
        method: error.config?.method?.toUpperCase(),
        contentType: error.config?.headers?.['Content-Type'],
        timeout: error.config?.timeout,
        errorCode: error.code,
        errorMessage: error.message,
      });

      scope.setContext('request_payload', {
        payloadSizeBytes,
        payloadSizeFormatted: formatBytes(payloadSizeBytes),
      });

      scope.setContext('request_headers', {
        'Content-Type': error.config?.headers?.['Content-Type'],
        Accept: error.config?.headers?.['Accept'],
        'x-ben-application': error.config?.headers?.['x-ben-application'],
      });

      scope.setContext('network_info', {
        onLine: navigator?.onLine,
        userAgent: navigator?.userAgent,
      });

      Sentry.captureException(error);
    });
  }

  return Promise.reject(error);
};

const responseInterceptor = (response: AxiosResponse<any>): any => {
  // Skip processing for blob responses
  if (response.config.responseType === 'blob') {
    return response;
  }

  if (response.data) {
    const schema = response.config.schema;
    const convertCaseShallowIgnoreKeys =
      response.config.convertCaseShallowIgnoreKeys;

    const camelCasedResponse = snakeToCamel(response.data, {
      shallowIgnoreKeys: convertCaseShallowIgnoreKeys,
    });

    response.data = camelCasedResponse;

    if (schema) {
      try {
        response.data = schema.parse(camelCasedResponse);
      } catch (error) {
        captureError(error);
      }
    }
  }
  return response;
};

const requestInterceptor = (request: AxiosRequestConfig): any => {
  if (request.headers?.['Content-Type'] === 'multipart/form-data') {
    // Do not convert multipart form data
    return request;
  }

  if (request.disableConvertCase) {
    // Do not convert to snake case when `disableConvertCase` is true
    return request;
  }

  if (request.params) {
    request.params = camelToSnake(request.params);
    request.paramsSerializer = { indexes: true };
  }

  if (request.data) {
    request.data = camelToSnake(request.data);
  }

  return request;
};

export const axios: AxiosInstance = baseAxios.create({
  baseURL: `${process.env.REACT_APP_AXIOS_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'x-ben-application': process.env.REACT_APP_NAME,
  },
});

axios.interceptors.response.use(responseInterceptor, networkErrorInterceptor);

axios.interceptors.request.use(requestInterceptor);

function removePathFromUrl(url: string): string {
  const parsedUrl = new URL(url);
  parsedUrl.pathname = '';
  return parsedUrl.toString();
}

export const orvalAxios: AxiosInstance = baseAxios.create({
  baseURL: removePathFromUrl(process.env.REACT_APP_AXIOS_API_URL),
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'x-ben-application': process.env.REACT_APP_NAME,
  },
});

orvalAxios.interceptors.response.use(
  responseInterceptor,
  networkErrorInterceptor
);

orvalAxios.interceptors.request.use(requestInterceptor);

// prettier-ignore
export const axiosOrvalCustomInstance = <T,>(
  config: AxiosRequestConfig
): Promise<T> => {
  return orvalAxios(config).then((response) => {
    return response?.data;
  });
};
